import React from "react";
import { createRoot } from "react-dom/client";
import debounce from "lodash/debounce";

const STARRED_KEY = "expo_starred";
const VISITED_KEY = "expo_visited";
const COLLECTION_URL = (key) => `/account/collection/${key}/`;
const AUTH_STATUS_URL = "/accounts/status/";

// Keys scoped per-user so two accounts sharing a browser don't leak.
const STORAGE_PREFIX = "expo-prefs";
const PENDING_SUFFIX = "pending";
const localKey = (userId, suffix) => `${STORAGE_PREFIX}:${userId}:${suffix}`;

const readLocal = (userId, key) => {
    try {
        const raw = localStorage.getItem(localKey(userId, key));
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};
const writeLocal = (userId, key, value) => {
    try {
        localStorage.setItem(localKey(userId, key), JSON.stringify(value));
    } catch (_) {
        // quota/private mode — accept silent loss
    }
};
const readPending = (userId) => readLocal(userId, PENDING_SUFFIX) || {};
const markPending = (userId, key, isPending) => {
    const pending = readPending(userId);
    if (isPending) pending[key] = true;
    else delete pending[key];
    writeLocal(userId, PENDING_SUFFIX, pending);
};

const sameIds = (a, b) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

async function fetchCollection(key) {
    const res = await fetch(COLLECTION_URL(key));
    if (!res.ok) throw new Error(`GET ${key} failed: ${res.status}`);
    const data = await res.json();
    return data.collection.map(String);
}

async function postCollection(key, ids) {
    const res = await fetch(COLLECTION_URL(key), {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ ids: ids.join(",") }),
    });
    if (!res.ok) {
        // `status` lets the caller distinguish server rejection from network failure.
        const err = new Error(`POST ${key} failed: ${res.status}`);
        err.status = res.status;
        throw err;
    }
}

// pending=true → push local up; pending=false → pull server down.
async function reconcile(userId, key, isPending, localIds, setLocal) {
    if (isPending) {
        try {
            await postCollection(key, localIds);
            markPending(userId, key, false);
        } catch (_) {
            // keep pending, keep local; the retryPending listeners will reattempt
        }
    } else {
        try {
            const fresh = await fetchCollection(key);
            setLocal(fresh);
            writeLocal(userId, key, fresh);
        } catch (_) {}
    }
}

class ExpoMap {
    constructor() {
        this.init();
    }

    init() {
        const expoMap = document.querySelector(".expo-map");
        if (!expoMap) {
            return;
        }
        Promise.all([
            import(/* webpackChunkName: "expo-map" */ "../react/ExpoMap/MapWrapper"),
            import(/* webpackChunkName: "expo-map" */ "easy-peasy"),
        ]).then(
            ([
                { default: MapWrapper },
                { createStore, StoreProvider, action, thunk },
            ]) => {
                let storeRef;
                const getUserId = () =>
                    storeRef.getState().preferences.userId;

                const makeFlush = (key, getIds, setLocal) =>
                    debounce(async () => {
                        const idsAtPost = getIds();
                        const userId = getUserId();
                        try {
                            await postCollection(key, idsAtPost);
                            if (!userId) return;
                            // Don't clear pending if a toggle landed during the POST —
                            // the next debounced flush owns this key now.
                            if (sameIds(idsAtPost, getIds())) {
                                markPending(userId, key, false);
                            }
                        } catch (err) {
                            if (!userId) return;
                            if (err.status) {
                                // Server rejected local state — resync from server.
                                markPending(userId, key, false);
                                try {
                                    const fresh = await fetchCollection(key);
                                    setLocal(fresh);
                                    writeLocal(userId, key, fresh);
                                } catch (_) {}
                            } else {
                                markPending(userId, key, true);
                            }
                        }
                    }, 1000);
                const flush = {
                    [STARRED_KEY]: makeFlush(
                        STARRED_KEY,
                        () => storeRef.getState().preferences.starred,
                        (ids) => storeRef.getActions().setStarred(ids),
                    ),
                    [VISITED_KEY]: makeFlush(
                        VISITED_KEY,
                        () => storeRef.getState().preferences.visited,
                        (ids) => storeRef.getActions().setVisited(ids),
                    ),
                };

                const store = createStore({
                    maps: [],
                    stands: [],
                    exhibitors: [],
                    categories: [],
                    features: [],
                    preferences: {
                        starred: [],
                        visited: [],
                        authenticated: false,
                        userId: null,
                        // Flipped true once loadPreferences resolves, so UI
                        // doesn't flash a logged-out state to a signed-in user.
                        loaded: false,
                    },
                    updateMaps: action((state, payload) => {
                        state.maps = payload.maps;
                        state.stands = payload.stands;
                        state.exhibitors = payload.exhibitors;
                        state.categories = payload.categories || [];
                        state.features = payload.features || [];
                    }),
                    setPreferences: action((state, payload) => {
                        Object.assign(state.preferences, payload);
                    }),
                    setStarred: action((state, ids) => {
                        state.preferences.starred = ids;
                    }),
                    setVisited: action((state, ids) => {
                        state.preferences.visited = ids;
                    }),
                    toggleStar: thunk((actions, pk, { getState }) => {
                        const id = String(pk);
                        const { preferences } = getState();
                        if (!preferences.authenticated) return;
                        const next = preferences.starred.includes(id)
                            ? preferences.starred.filter((x) => x !== id)
                            : [...preferences.starred, id];
                        actions.setStarred(next);
                        if (preferences.userId) {
                            // Persist immediately so a tab close before the
                            // debounced POST fires doesn't lose the change.
                            writeLocal(preferences.userId, STARRED_KEY, next);
                            markPending(preferences.userId, STARRED_KEY, true);
                        }
                        flush[STARRED_KEY]();
                    }),
                    toggleVisited: thunk((actions, pk, { getState }) => {
                        const id = String(pk);
                        const { preferences } = getState();
                        if (!preferences.authenticated) return;
                        const next = preferences.visited.includes(id)
                            ? preferences.visited.filter((x) => x !== id)
                            : [...preferences.visited, id];
                        actions.setVisited(next);
                        if (preferences.userId) {
                            writeLocal(preferences.userId, VISITED_KEY, next);
                            markPending(preferences.userId, VISITED_KEY, true);
                        }
                        flush[VISITED_KEY]();
                    }),
                    loadPreferences: thunk(async (actions) => {
                        let auth;
                        try {
                            const res = await fetch(AUTH_STATUS_URL);
                            auth = await res.json();
                        } catch (_) {
                            auth = { authenticated: false };
                        }
                        if (!auth.authenticated) {
                            actions.setPreferences({
                                starred: [],
                                visited: [],
                                authenticated: false,
                                userId: null,
                                loaded: true,
                            });
                            return;
                        }
                        const userId = String(auth.user_id);
                        // Hydrate from localStorage first so an offline reload
                        // shows the last-known collections without waiting.
                        const localStarred =
                            readLocal(userId, STARRED_KEY) || [];
                        const localVisited =
                            readLocal(userId, VISITED_KEY) || [];
                        actions.setPreferences({
                            starred: localStarred,
                            visited: localVisited,
                            authenticated: true,
                            userId,
                            loaded: true,
                        });
                        const pending = readPending(userId);
                        await Promise.all([
                            reconcile(
                                userId,
                                STARRED_KEY,
                                !!pending[STARRED_KEY],
                                localStarred,
                                (ids) => actions.setStarred(ids),
                            ),
                            reconcile(
                                userId,
                                VISITED_KEY,
                                !!pending[VISITED_KEY],
                                localVisited,
                                (ids) => actions.setVisited(ids),
                            ),
                        ]);
                    }),
                });
                storeRef = store;

                // Reattempt pending flushes on reconnect / tab focus —
                // the on-mount reconcile only covers fresh page loads.
                const retryPending = () => {
                    const { preferences } = storeRef.getState();
                    if (!preferences.userId) return;
                    const pending = readPending(preferences.userId);
                    if (pending[STARRED_KEY]) flush[STARRED_KEY]();
                    if (pending[VISITED_KEY]) flush[VISITED_KEY]();
                };
                window.addEventListener("online", retryPending);
                document.addEventListener("visibilitychange", () => {
                    if (!document.hidden) retryPending();
                });

                const root = createRoot(expoMap);

                root.render(
                    <StoreProvider store={store}>
                        <MapWrapper />
                    </StoreProvider>
                );
            }
        );
    }
}

export default ExpoMap;
